import { useMemo, useState, useEffect, useRef } from 'react';
import { Paper, List, ListItemButton, ListItemText, Typography, Box, TextField } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { Node } from '../peregrineFlowInterfaces';
import useStore from '../store';

interface QuickNodeAddProps {
    visible: boolean;
    position: { x: number, y: number };
    availableNodes: Node[];
    filterInputTypes: string[];
    filterOutputTypes: string[];
    onSelectNode: (node: Node) => void;
    onClose: () => void;
}

export const QuickNodeAdd = ({ visible, position, availableNodes, filterInputTypes, filterOutputTypes, onSelectNode, onClose }: QuickNodeAddProps) => {
    const [searchQuery, setSearchQuery] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const isGuest = useStore((state) => state.isGuest);

    useEffect(() => {
        if (visible) {
            setSearchQuery('');
            setTimeout(() => {
                inputRef.current?.focus();
            }, 50);
        }
    }, [visible]);

    const filteredNodes = useMemo(() => {
        if (!visible) return [];

        let nodes = availableNodes.filter(node => {
             if (filterInputTypes.length > 0) {
                // Dragged from Output (Source). Looking for compatible Inputs (Target).
                // filterInputTypes = Source Types.

                // If Source outputs Any, any node with inputs is a match
                if (filterInputTypes.includes('Any')) {
                    return node.input_ports.length > 0;
                }

                return node.input_ports.some(p => {
                    const pTypes = Array.isArray(p.data_type) ? p.data_type : [p.data_type];
                    // If Target accepts Any, it's a match.
                    if (pTypes.includes('Any')) return true;
                    // Otherwise check overlap
                    return pTypes.some(t => filterInputTypes.includes(t));
                });
            }
            if (filterOutputTypes.length > 0) {
                // Dragged from Input (Target). Looking for compatible Outputs (Source).
                // filterOutputTypes = Target Types.

                // If Target (dragged input) accepts Any, ANY output is a match.
                if (filterOutputTypes.includes('Any')) return true;

                return node.output_ports.some(p => {
                    const pTypes = Array.isArray(p.data_type) ? p.data_type : [p.data_type];
                    if (pTypes.includes('Any')) return true;
                    // Otherwise check overlap
                    return pTypes.some(t => filterOutputTypes.includes(t));
                });
            }
            return false;
        });

        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            nodes = nodes.filter(node =>
                node.display_name.toLowerCase().includes(query) ||
                node.group.toLowerCase().includes(query) ||
                (node.description && node.description.toLowerCase().includes(query))
            );
        }

        return nodes;
    }, [availableNodes, filterInputTypes, filterOutputTypes, visible, searchQuery]);

    if (!visible) return null;

    return (
        <>
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }} onClick={onClose} />
            <Paper
                sx={{
                    position: 'absolute',
                    left: position.x,
                    top: position.y,
                    zIndex: 999,
                    maxHeight: 300,
                    overflow: 'auto',
                    width: 250
                }}
                elevation={3}
            >
                <Box p={1} borderBottom={1} borderColor="divider">
                    <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                        Suggested Nodes
                    </Typography>
                    <TextField
                        inputRef={inputRef}
                        size="small"
                        fullWidth
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoFocus
                    />
                </Box>
                <List dense>
                    {filteredNodes.map(node => (
                        <ListItemButton
                            key={node.name}
                            onClick={() => onSelectNode(node)}
                            sx={{
                                opacity: isGuest && node.is_premium ? 0.6 : 1
                            }}
                        >
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <span>{node.display_name}</span>
                                        {isGuest && node.is_premium && (
                                            <LockIcon
                                                sx={{
                                                    fontSize: '14px',
                                                    color: '#333333',
                                                    opacity: 1
                                                }}
                                            />
                                        )}
                                    </Box>
                                }
                                secondary={node.group}
                            />
                        </ListItemButton>
                    ))}
                    {filteredNodes.length === 0 && (
                        <Box p={2}>
                            <Typography variant="body2" color="textSecondary">No compatible nodes found</Typography>
                        </Box>
                    )}
                </List>
            </Paper>
        </>
    );
};
